import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Building2, Search } from 'lucide-react'

import { api } from '@/api/client'
import type { PartnerGroup } from '@/types/models'
import { Modal } from './Modal'
import { cn } from '@/lib/utils'

export type QuoteTarget = {
  type: 'group'; id: number; name: string; member_count: number
}

interface Props {
  value: { id: number } | null
  onChange: (t: QuoteTarget) => void
  placeholder?: string
}

/** 상위 거래처 선택기. 모든 매출처는 상위에 소속되므로 상위(그룹)만 선택. */
export function QuoteTargetPicker({ value, onChange, placeholder = '상위 거래처 선택...' }: Props) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [label, setLabel] = useState('')
  const [activeIdx, setActiveIdx] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!value) { setLabel(''); return }
    api<PartnerGroup>(`/api/partner-groups/${value.id}`)
      .then((g) => setLabel(`${g.name} · ${g.member_count}개`))
      .catch(() => {})
  }, [value?.id])

  const { data: groups = [] } = useQuery({
    queryKey: ['target-picker-groups', search],
    queryFn: () => {
      const params = new URLSearchParams()
      if (search) params.set('q', search)
      return api<PartnerGroup[]>(`/api/partner-groups?${params}`)
    },
    enabled: open,
    staleTime: 30_000,
  })

  useEffect(() => { setActiveIdx(0) }, [search, open])

  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-picker-idx="${activeIdx}"]`,
    )
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIdx, open, groups.length])

  function selectGroup(g: PartnerGroup) {
    setLabel(`${g.name} · ${g.member_count}개`)
    onChange({ type: 'group', id: g.id, name: g.name, member_count: g.member_count })
    setOpen(false)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => Math.min(i + 1, Math.max(groups.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Home') {
      e.preventDefault(); setActiveIdx(0)
    } else if (e.key === 'End') {
      e.preventDefault(); setActiveIdx(Math.max(groups.length - 1, 0))
    } else if (e.key === 'Enter') {
      const g = groups[activeIdx]
      if (g) { e.preventDefault(); selectGroup(g) }
    }
    // Escape 는 Modal 컴포넌트의 window 리스너가 스택 기반으로 처리.
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-left bg-white hover:bg-slate-50 inline-flex items-center gap-2"
      >
        <Building2 className={cn('w-3.5 h-3.5 shrink-0', label ? 'text-blue-500' : 'text-slate-400')}/>
        <span className={cn('truncate', label ? 'text-slate-900' : 'text-slate-400')}>
          {label || placeholder}
        </span>
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="상위 거래처 선택">
        <div className="relative mb-3">
          <input
            type="text" autoFocus value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="이름, 사업자번호"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
        </div>

        <div ref={listRef} className="max-h-[420px] overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
          {groups.map((g, i) => {
            const selected = value?.id === g.id
            const active = i === activeIdx
            return (
              <button
                key={`g-${g.id}`}
                type="button"
                data-picker-idx={i}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => selectGroup(g)}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm',
                  active
                    ? 'bg-blue-100'
                    : selected
                      ? 'bg-blue-50'
                      : 'hover:bg-blue-50',
                )}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5 text-blue-500"/>
                  <span className="font-medium">{g.name}</span>
                  <span className="ml-auto text-xs text-slate-500 tabular-nums">{g.member_count}개 자식</span>
                </div>
                {g.biz_no && (
                  <div className="text-xs text-slate-400 tabular-nums mt-0.5 ml-6">{g.biz_no}</div>
                )}
              </button>
            )
          })}
          {groups.length === 0 && (
            <div className="text-center text-slate-400 py-8 text-sm">검색 결과 없음</div>
          )}
        </div>

        <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
          <span>상위 거래처 선택 시 → 자식 매출처 전부에 자동 적용.</span>
          <span className="text-slate-400">↑↓ 이동 · Enter 선택 · Esc 닫기</span>
        </div>
      </Modal>
    </>
  )
}
